<script setup lang="ts">
import { Loading } from "@app/Components/Layout/Loading";
import { CarbucksLogo } from '@app/Components/CarbucksLogo';
import { onMounted, onUnmounted, ref, computed, watch } from 'vue';

// Define props interface
type Props = { loadingMessages?: string[]; }
const { loadingMessages = [ 'Loading...'] } = defineProps<Props>();

const currentMessageIndex = ref( 0 );
const isTransitioning = ref( false );

/** 
 * Stores the interval ID for message rotation, so it can be cleared/reset.
 * @type {Ref<number | null>}
 */
const intervalId = ref<number | null>( null );

/**
 * The currently displayed message, computed from the messages array and index.
 */
const currentMessage = computed( () => loadingMessages[currentMessageIndex.value] );

/**
 * Handles the transition between messages with a slide-up animation.
 * The sequence is:
 * 1. Start transition (fade out current message)
 * 2. Wait 500ms for fade out to complete
 * 3. Change to next message
 * 4. Wait 50ms for DOM update
 * 5. End transition (fade in new message)
 * The isTransitioning flag prevents multiple transitions from occurring simultaneously.
 * Will do nothing if there is only one message.
 */
const nextMessage = () =>
{
    // Only transition if there are at least 2 messages
    if (
        isTransitioning.value ||
        loadingMessages.length <= 1
    ) {
        return;
    }

    isTransitioning.value = true;

    setTimeout( () => {
        // Increment message index and wrap around to start if we reach the end
        currentMessageIndex.value = ( currentMessageIndex.value + 1 ) % loadingMessages.length;

        // Wait for the new message to be in place before allowing next transition
        setTimeout( () => {
            isTransitioning.value = false;
        }, 50 );
    }, 500 );
};

/**
 * Sets up the interval for rotating messages.
 * Only starts the interval if there are at least two messages.
 * Clears any existing interval before starting a new one.
 */
function setupInterval()
{
    // Clear any existing interval
    if( intervalId.value !== null )
    {
        clearInterval(intervalId.value);
        intervalId.value = null;
    }
    // Only set interval if there are at least 2 messages
    if( loadingMessages.length > 1 )
    {
        intervalId.value = window.setInterval( nextMessage, 2000 );
    }
}

/**
 * Watches for changes in the number of messages.
 * Resets the message index and transition state, and re-sets the interval.
 */
watch( () => loadingMessages.length, () =>
    {
        currentMessageIndex.value = 0;
        isTransitioning.value = false;
        setupInterval();
    }
);

/**
 * Sets up the message rotation interval when the component is mounted.
 */
onMounted( () =>
{
    setupInterval();
});

/**
 * Cleans up the message rotation interval when the component is unmounted.
 */
onUnmounted( () =>
{
    if( intervalId.value !== null )
    {
        clearInterval(intervalId.value);
    }
});

</script>

<template>
    <div class="flex flex-col min-h-full h-full w-full">
        <!-- LOGO -->
        <div class="flex-1 flex flex-col items-center justify-center">
            <div class="flex flex-col items-center gap-8 pt-12 2xl:pt-16">
                <!-- <CarbucksLogo :size="96" /> -->

                <Loading />

                <div class="flex flex-col items-center gap-4">
                    <div class="relative h-14 overflow-hidden">
                        <div class="text-lg text-center px-4 tracking-wider
                                    transition-all ease-in-out"
                             :class="{ 
                                'translate-y-0 opacity-100': !isTransitioning,
                                '-translate-y-full opacity-0': isTransitioning 
                             }">
                            {{ currentMessage }}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
